import React from "react";
import { CLOSE_GLYPH_PATH, CLOSE_GLYPH_VIEWBOX } from "../close-glyph";
import styles from "./chip.module.css";
import { withSafeRender } from "../safe-render";

/**
 * Chip — one chosen value with a button that removes it.
 *
 * Server Component: no state and no handlers. The remove control is a real
 * `<button type="button">` carrying `value`, so a parent removes chips from ONE
 * delegated click listener on its list instead of a handler per chip. Removing
 * a chip removes its focused button, so the parent owns where focus goes next.
 *
 * Source: ECLIPSE-Organisms, component 15268:27009 (Property 1=Default) in the
 * "Input chip" frame 15268:27010.
 */
export interface ChipProps {
  /** The chip's visible text, e.g. a channel name. Empty or blank renders nothing. */
  label: string;
  /** Accessible name of the remove button. Defaults to `Remove ${label}` when absent or blank. */
  removeLabel?: string;
  /** Written to the remove button's `value` attribute so a parent can identify the chip from a delegated click. Omitted when absent. */
  value?: string;
}

function ChipImpl({ label, removeLabel, value }: ChipProps) {
  /* CMS and API data never pass through TypeScript, so a missing label arrives
     as anything; only a real, non-blank string renders. */
  const text = typeof label === "string" ? label.trim() : "";
  if (!text) {
    return null;
  }

  const name =
    (typeof removeLabel === "string" && removeLabel.trim()) || `Remove ${text}`;

  return (
    <span className={styles.chip}>
      {/* The Text atom's global type classes, not the atom: Text parses markup
          in a string, and a label is plain data whose visible text must equal
          the one the button's name is built from. */}
      <span className={`dds-text dds-body-2 dds-tone-primary ${styles.label}`}>{text}</span>
      <button aria-label={name} className={styles.remove} type="button" value={value}>
        <svg
          aria-hidden="true"
          className={styles.glyph}
          focusable="false"
          viewBox={CLOSE_GLYPH_VIEWBOX}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d={CLOSE_GLYPH_PATH} fill="currentColor" />
        </svg>
      </button>
    </span>
  );
}
export const Chip = withSafeRender(ChipImpl, "Chip");

export default Chip;
